import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";

const mapFacultyOptions = (items = []) =>
  items.map((item) => ({
    facultyId: item._id,
    name: item.name,
  }));

const mapDepartmentOptions = (items = []) =>
  items.map((item) => ({
    deptId: item._id,
    facultyId: item.facultyId,
    name: item.name,
  }));

const PROGRAM_TYPES = [
  { value: "undergraduate", label: "undergraduate" },
  { value: "postgraduate", label: "postgraduate" },
  { value: "trainings", label: "trainings" },
  { value: "workshops", label: "workshops" },
  { value: "pmysdp-batch-3", label: "PMYSDP - Batch 3" },
  { value: "pmysdp-batch-3-extension", label: "PMYSDP - Batch 3 Extension" },
];

const SDC_PROGRAM_TYPES = [
  "trainings",
  "workshops",
  "pmysdp-batch-3",
  "pmysdp-batch-3-extension",
];

const isSdcProgramType = (type) => SDC_PROGRAM_TYPES.includes(type);

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const Courses = () => {
  const initialFormState = {
    img: "",
    title: "",
    description: "",
    type: "",
    faculty: "",
    dept: "",
    courseCode: "",
    courseDuration: "",
    courseFee: "",
    feeStructure: "",
    courseDetails: "",
    schemeOfStudies: "",
    schemeOfStudiesFile: "",
    admissionStart: "",
    admissionEnd: "",
    admissionOpen: false,
    eligibility: "",
    formLink: "",
  };

  const [courses, setCourses] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCourseId, setEditCourseId] = useState("");
  const [sortByLatest, setSortByLatest] = useState(false);
  const [filter, setFilter] = useState("all");
  const [formData, setFormData] = useState(initialFormState);
  const [imgFile, setImgFile] = useState(null);
  const [sosFile, setSosFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [deptArray, setDeptArray] = useState([]);
  const [facultiesArray, setFacultiesArray] = useState([]);
  const [departmentsArray, setDepartmentsArray] = useState([]);
  const [titleError, setTitleError] = useState("");

  useEffect(() => {
    if (formData.admissionStart && formData.admissionEnd) {
      const today = new Date();
      const startDate = new Date(formData.admissionStart);
      const endDate = new Date(formData.admissionEnd);

      const isOpen = startDate <= today && today <= endDate;

      setFormData((prevState) => ({
        ...prevState,
        admissionOpen: isOpen,
      }));
    }
  }, [formData.admissionStart, formData.admissionEnd]);
  useEffect(() => {
    fetchCourses();
    fetchFacultyOptions();
  }, []);

  const fetchFacultyOptions = async () => {
    try {
      const [facultyRes, departmentRes] = await Promise.all([
        axiosInstance.get("faculty/all"),
        axiosInstance.get("department/all"),
      ]);
      setFacultiesArray(mapFacultyOptions(facultyRes?.data?.data));
      setDepartmentsArray(mapDepartmentOptions(departmentRes?.data?.data));
    } catch (error) {
      console.error("Error fetching faculties/departments:", error);
    }
  };

  const fetchCourses = async () => {
    setAllLoading(true);
    try {
      const response = await axiosInstance.get("program/all");
      setCourses(response?.data?.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error fetching courses!',
        text: 'Please try again.',
        timer: 1500,
      });
    } finally {
      setAllLoading(false);
    }
  };
  const handleDelete = async (id) => {
    try {

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (result.isConfirmed) {
        await axiosInstance.delete(`program/delete/${id}`);
        Swal.fire({
          icon: 'success',
          title: 'Course deleted successfully!',
          showConfirmButton: false,
          timer: 1500,
        });
        fetchCourses();
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error deleting course!',
        text: 'Please try again.',
        timer: 1500,
      });
    } finally {

    }
  };
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setLoading(true);
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "img" || key === "schemeOfStudiesFile") {
        return;
      }
      formDataToSend.append(key, formData[key]);
    });

    if (imgFile) {
      formDataToSend.append("img", imgFile);
    }
    if (sosFile) {
      formDataToSend.append("scheme_of_studies_file", sosFile);
    }
    try {
      await axiosInstance.post("program/create", formDataToSend);

      fetchCourses();
      Swal.fire({
        icon: 'success',
        title: 'Course created successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
      setShowCreateModal(false);
      setFormData(initialFormState);
      setImgFile(null);
      setSosFile(null);
    } catch (error) {
      console.error("Error creating course:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error creating course!',
        text: error.response?.data?.message || 'Please try again.',
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };
  const filteredCourses = courses.filter((course) =>
    filter === "all" ? true : course.type === filter
  );
  const handleEditCourse = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setLoading(true);
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "img" || key === "schemeOfStudiesFile") {
        return;
      }
      formDataToSend.append(key, formData[key]);
    });

    if (imgFile) {
      formDataToSend.append("img", imgFile);
    }
    if (sosFile) {
      formDataToSend.append("scheme_of_studies_file", sosFile);
    }
    formDataToSend.append("_method", "PUT");
    try {
      await axiosInstance.post(`program/change/${editCourseId}`, formDataToSend);

      fetchCourses();
      Swal.fire({
        icon: 'success',
        title: 'Course updated successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
      setShowEditModal(false);
      setFormData(initialFormState);
      setImgFile(null);
      setSosFile(null);
    } catch (error) {
      console.error("Error updating course:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error updating course!',
        text: error.response?.data?.message || 'Please try again.',
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchProgram = async (id) => {
    try {
      setShowEditModal(true);
      setEditCourseId(id);
      setImgFile(null);
      setSosFile(null);
      const response = await axiosInstance.get(`program/single/${id}`);
      const data = response?.data?.data;

      setFormData({
        img: data.img || "",
        title: data.title || "",
        description: data.description || "",
        type: data.type || "",
        faculty: data.faculty || "",
        dept: data.dept || "",
        courseCode: data.courseCode || "",
        courseDuration: data.courseDuration || "",
        courseFee: data.courseFee || "",
        feeStructure: data.feeStructure || "",
        courseDetails: data.courseDetails || "",
        schemeOfStudies: data.schemeOfStudies || "",
        schemeOfStudiesFile: data.schemeOfStudiesFile || "",
        admissionStart: data.admissionStart || "",
        admissionEnd: data.admissionEnd || "",
        admissionOpen: data.admissionOpen || false,
        eligibility: data.eligibility || "",
        formLink: data.formLink || "",
      });
    } catch (error) {
      console.error("Error fetching program:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error fetching program!',
        text: 'Please try again.',
        timer: 1500,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "title") {
      setTitleError("");
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "faculty") {
      const matchingDepartments = departmentsArray.filter(
        (dept) => String(dept.facultyId) === String(value)
      );
      setDeptArray(matchingDepartments);
    }
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > MAX_SIZE_MB) {
        alert(`File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`);
        e.target.value = "";
      } else {
        setImgFile(file);
      }
    }
  };
  const handleSosFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSosFile(file);
    }
  };
  const sortedCourses = sortByLatest
  ? [...filteredCourses].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  : filteredCourses;
  return (
    <div>
      <div style = {{display: "flex", justifyContent: "space-between"}}>
      <button
        onClick={() => setShowCreateModal(true)}
        style={{
          padding: "10px 20px",
          backgroundColor: "#4CAF50",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          marginBottom: "20px",
          cursor: "pointer",
        }}
      >
        Create New Program
      </button>
        <button
          onClick={() => setSortByLatest((prev) => !prev)}
          style={{
            padding: "10px 20px",
            backgroundColor: sortByLatest ? "#2196F3" : "#f0f0f0",
            color: sortByLatest ? "#fff" : "#000",
            border: "none",
            borderRadius: "5px",
            marginBottom: "20px",
            cursor: "pointer",
            marginRight: "150px",
          }}
        >
          {sortByLatest ? "Show Default" : "Show Latest"}
        </button>
      </div>
      <div style={{ marginBottom: "20px" }}>
        <button
          onClick={() => setFilter("all")}
          style={{
            padding: "10px 20px",
            marginRight: "10px",
            backgroundColor: filter === "all" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          All
        </button>
        <button
          onClick={() => setFilter("undergraduate")}
          style={{
            padding: "10px 20px",
            marginRight: "10px",
            backgroundColor: filter === "undergraduate" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          undergraduate
        </button>
        <button
          onClick={() => setFilter("postgraduate")}
          style={{
            padding: "10px 20px",
            backgroundColor: filter === "postgraduate" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          postgraduate
        </button>
        <button
          onClick={() => setFilter("trainings")}
          style={{
            padding: "10px 20px",
            backgroundColor: filter === "trainings" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginLeft: "10px",
          }}
        >
          trainings
        </button>
        <button
          onClick={() => setFilter("workshops")}
          style={{
            padding: "10px 20px",
            backgroundColor: filter === "workshops" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginLeft: "10px",
          }}
        >
          workshops
        </button>
        <button
          onClick={() => setFilter("pmysdp-batch-3")}
          style={{
            padding: "10px 20px",
            backgroundColor: filter === "pmysdp-batch-3" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginLeft: "10px",
          }}
        >
          PMYSDP - Batch 3
        </button>
        <button
          onClick={() => setFilter("pmysdp-batch-3-extension")}
          style={{
            padding: "10px 20px",
            backgroundColor:
              filter === "pmysdp-batch-3-extension" ? "#4CAF50" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginLeft: "10px",
          }}
        >
          PMYSDP - Batch 3 Extension
        </button>
      </div>
      {allLoading ? <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
      }}> <CircularProgress /> </div> : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
          {courses.length > 0 ? sortedCourses.map((course) => (
            <div
              key={course._id}
              style={{
                width: "250px",
                padding: "15px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
            >

              {/* <img src={course.img} alt={course.title} style={{ width: "100%" }} /> */}

              <h3>{course.title}</h3>
              <p>{course.faculty}</p>
              <p>{course.courseCode}</p>
              <Actions
                courseId={course._id}
                onEdit={fetchProgram}
                onDelete={handleDelete}
              />
            </div>
          )) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>No Programs found.</p>
            </div>

          )}
        </div>
      )}

      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => {
          setShowCreateModal(false);
          setFormData(initialFormState);
          setImgFile(null);
          setSosFile(null);
        }}
        style={customModalStyles}
        contentLabel="Create New Course"
      >
        <h2>Create New Course</h2>
        <form onSubmit={handleCreateCourse}>
          {[
            { label: "Title", name: "title", type: "text", required: true },
            { label: "Description", name: "description", type: "textarea" },
            { label: "Type", name: "type", type: "dropdown", required: true, options: PROGRAM_TYPES },
            { label: "Faculty", name: "faculty", type: "dropdown", required: true, options: facultiesArray },
            { label: "Department", name: "dept", type: "dropdown", required: true, options: deptArray },
            {
              label: formData.type === "workshops" ? "Date & Time" : "Course Duration",
              name: "courseDuration",
              type: "text",
            },
            { label: "Total Course Fee", name: "courseFee", type: "text" },
            {
              label: "Fee Structure Breakdown",
              name: "feeStructure",
              type: "textarea",
              placeholder: "e.g. Admission Fee: 10,000 | Tuition Fee: 45,000 | Exam Fee: 5,000 per semester",
            },
            {
              label: formData.type === "workshops" ? "Session Details" : "Course Details",
              name: "courseDetails",
              type: "textarea",
            },
            {
              label: "Scheme of Studies (Text Overview / Syllabus)",
              name: "schemeOfStudies",
              type: "textarea",
              placeholder: "Semester 1:\n- Course 1\n- Course 2\n\nSemester 2:\n- Course 3\n- Course 4",
            },
            { label: "Eligibility Criteria", name: "eligibility", type: "textarea" },
            {
              label: formData.type === "workshops" ? "Registration Portal" : "Form Link",
              name: "formLink",
              type: "text",
            },
            {
              label: formData.type === "workshops" ? "Registration Deadline" : "Registration / Admission End",
              name: "admissionEnd",
              type: "text",
            },
          ].map(({ label, name, type, required, options, placeholder }) => (
            <div key={name} style={{ marginBottom: "10px" }}>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                {label}
              </label>
              {name === "dept" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={isSdcProgramType(formData.type) ? false : required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="" disabled>Select an option</option>
                  {options?.map((option) => (
                    <option key={option.deptId} value={option.deptId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              ) : name === "faculty" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={isSdcProgramType(formData.type) ? false : required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="" disabled>Select an option</option>
                  {options?.map((option) => (
                    <option key={option.facultyId} value={option.facultyId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              ) : type === "dropdown" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">Select an option</option>
                  {options?.map((option) => {
                    const value = typeof option === "object" ? option.value : option;
                    const label = typeof option === "object" ? option.label : option;
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    height: "100px",
                  }}
                />
              ) : (
                <input
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              )}
              {name === "title" && titleError && (
                <span style={{ color: "red", fontSize: "12px" }}>{titleError}</span>
              )}
            </div>
          ))}
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
              Upload Flyer / Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
              Upload Scheme of Studies (PDF / Document)
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleSosFileChange}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <button
            type="submit"
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              marginTop: "10px",
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Create"}
          </button>
        </form>
      </Modal>
      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setShowEditModal(false);
          setFormData(initialFormState);
          setImgFile(null);
          setSosFile(null);
        }}
        style={customModalStyles}
        contentLabel="Edit Program"
      >
        <h2>Edit Program</h2>
        <form onSubmit={handleEditCourse}>
          {[
            { label: "Title", name: "title", type: "text" },
            { label: "Description", name: "description", type: "textarea" },
            { label: "Type", name: "type", type: "dropdown", required: true, options: PROGRAM_TYPES },
            { label: "Faculty", name: "faculty", type: "dropdown", required: true, options: facultiesArray },
            { label: "Department", name: "dept", type: "dropdown", required: true, options: deptArray },
            {
              label: formData.type === "workshops" ? "Date & Time" : "Course Duration",
              name: "courseDuration",
              type: "text",
            },
            { label: "Total Course Fee", name: "courseFee", type: "text" },
            {
              label: "Fee Structure Breakdown",
              name: "feeStructure",
              type: "textarea",
              placeholder: "e.g. Admission Fee: 10,000 | Tuition Fee: 45,000 | Exam Fee: 5,000 per semester",
            },
            {
              label: formData.type === "workshops" ? "Session Details" : "Course Details",
              name: "courseDetails",
              type: "textarea",
            },
            {
              label: "Scheme of Studies (Text Overview / Syllabus)",
              name: "schemeOfStudies",
              type: "textarea",
              placeholder: "Semester 1:\n- Course 1\n- Course 2\n\nSemester 2:\n- Course 3\n- Course 4",
            },
            { label: "Eligibility Criteria", name: "eligibility", type: "textarea" },
            {
              label: formData.type === "workshops" ? "Registration Portal" : "Form Link",
              name: "formLink",
              type: "text",
            },
            {
              label: formData.type === "workshops" ? "Registration Deadline" : "Registration / Admission End",
              name: "admissionEnd",
              type: "text",
            },
          ].map(({ label, name, type, required, options, placeholder }) => (
            <div key={name} style={{ marginBottom: "10px" }}>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                {label}
              </label>
              {name === "dept" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={isSdcProgramType(formData.type) ? false : required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="" disabled>Select an option</option>
                  {options?.map((option) => (
                    <option key={option.deptId} value={option.deptId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              ) : name === "faculty" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={isSdcProgramType(formData.type) ? false : required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="" disabled>Select an option</option>
                  {options?.map((option) => (
                    <option key={option.facultyId} value={option.facultyId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              ) : type === "dropdown" ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">Select an option</option>
                  {options?.map((option) => {
                    const value = typeof option === "object" ? option.value : option;
                    const label = typeof option === "object" ? option.label : option;
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    height: "100px",
                  }}
                />
              ) : (
                <input
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  required={required}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              )}
              {name === "title" && titleError && (
                <span style={{ color: "red", fontSize: "12px" }}>{titleError}</span>
              )}
            </div>
          ))}
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
              Upload New Flyer / Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
              Upload New Scheme of Studies (PDF / Document)
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleSosFileChange}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            {formData.schemeOfStudiesFile && (
              <div style={{ marginTop: "5px", fontSize: "12px", color: "#555" }}>
                Current File: <a href={formData.schemeOfStudiesFile} target="_blank" rel="noreferrer" style={{ color: "#2196F3" }}>View Scheme of Studies</a>
              </div>
            )}
          </div>
          <button
            type="submit"
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              marginTop: "10px",
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Update"}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default Courses;
